import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Switch } from "antd";
import JSONEditor from "jsoneditor";
import "jsoneditor/dist/jsoneditor.css";

const Editor = ({
  value,
  onChange,
  mode = "tree",
  name,
  enableClipboard = true,
  navigationBar = true,
  mainMenuBar = true,
}) => {
  const containerRef = useRef(null);
  const editorRef = useRef(null);
  const lastValueRef = useRef(value);

  // create/destroy instance
  useEffect(() => {
    if (!containerRef.current) return;

    editorRef.current = new JSONEditor(containerRef.current, {
      mode,
      name,
      enableClipboard,
      navigationBar,
      mainMenuBar,
      onChange: () => {
        // jsoneditor throws while user is typing invalid JSON in "code" mode
        try {
          const data = editorRef.current.get();
          lastValueRef.current = data;
          onChange && onChange(data);
        } catch {
          // ignore until JSON becomes valid again
        }
      },
    });

    // initial value
    editorRef.current.set(value);

    return () => {
      editorRef.current && editorRef.current.destroy();
      editorRef.current = null;
    };
    // this component is keyed by mode in your parent, so remounts on toggle
    // which is fine and avoids manual setMode sync issues.
  }, []); // intentionally mount once per key

  // external value changes (when reopening modal, etc.)
  useEffect(() => {
    if (!editorRef.current) return;
    // avoid feedback loop if value came from our own onChange
    if (value !== lastValueRef.current) {
      editorRef.current.update(value);
      lastValueRef.current = value;
    }
  }, [value]);

  // Give the editor a height or it can appear blank depending on layout
  return <div ref={containerRef} style={{ height: "60vh" }} />;
};
/* ---------------------------------------------------------------- */

const MetadataEditorModal = ({
  name = "Metadata",
  visible,
  onClose,
  metadata,
  onUpdateMetadata,
}) => {
  const [tempMetadata, setTempMetadata] = useState(metadata);
  const [editorMode, setEditorMode] = useState("tree");

  // Reset tempMetadata whenever the modal is opened or when metadata changes
  useEffect(() => {
    if (visible) {
      setTempMetadata(metadata);
    }
  }, [visible, metadata]);

  const handleSave = () => {
    // Save changes back to parent
    onUpdateMetadata(tempMetadata);
    onClose();
  };

  const handleModeToggle = (checked) => {
    setEditorMode(checked ? "tree" : "code");
  };

  return (
    <Modal
      title="Edit Metadata"
      open={visible}
      footer={null}
      onCancel={onClose}
      width="70%"
      destroyOnHidden
      maskClosable={false}
    >
      <div className="flex items-center mb-4">
        <span className="mr-2">Tree Mode</span>
        <Switch
          checked={editorMode === "tree"}
          onChange={handleModeToggle}
          checkedChildren="Tree"
          unCheckedChildren="Code"
        />
      </div>
      <Editor
        key={`json-editor-${editorMode}`}
        value={tempMetadata}
        onChange={(json) => setTempMetadata(json)}
        // You can set different modes: 'tree', 'code', 'text', or 'view'
        mode={editorMode}
        name={name}
        enableClipboard={true}
        navigationBar={true}
        mainMenuBar={true}
      />
      <div className="flex justify-end mt-4">
        <Button className="mr-2" onClick={onClose}>
          Cancel
        </Button>
        <Button type="primary" onClick={handleSave}>
          Save
        </Button>
      </div>
    </Modal>
  );
};

export default MetadataEditorModal;
